import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createExpiringSessionStorage, ONE_DAY_MS } from './expiring-storage';
import { track } from '@/lib/analytics/client';
import { buildAddToCart, hotelToday } from '@/lib/analytics/ecommerce';

export interface SelectionItem {
  roomSlug: string;
  roomName: string;
  roomImageUrl: string | null;
  roomTypeName: string | null;
  /**
   * English names for analytics. Set ONLY by the visitor-facing add buttons
   * (room cards, room hero) — its presence is what makes an add count as an
   * `add_to_cart`. Programmatic adds (checkout restore / legacy URL) omit it.
   */
  analytics?: { nameEn: string; typeEn: string | null };
}

/** Report a visitor-initiated add (see SelectionItem.analytics). */
function trackAdd(room: SelectionItem, checkIn: string | null, checkOut: string | null) {
  if (!room.analytics) return;
  track(
    buildAddToCart(
      { slug: room.roomSlug, nameEn: room.analytics.nameEn, typeEn: room.analytics.typeEn },
      { checkIn, checkOut, today: hotelToday() },
    ),
  );
}

interface BookingStore {
  // Shared dates for the entire selection
  checkIn: string | null;   // "YYYY-MM-DD"
  checkOut: string | null;  // "YYYY-MM-DD"
  // Selected rooms (no per-item dates — all share checkIn/checkOut)
  items: SelectionItem[];
  // True when the user pressed "Book Now" for a single room (not "Add Room").
  // Prevents the multi-room SelectionBar from appearing during navigation
  // and allows cleanup on back-navigation from checkout.
  singleRoomCheckout: boolean;

  setDates: (checkIn: string, checkOut: string) => void;
  addRoom: (room: SelectionItem) => void;
  removeRoom: (roomSlug: string) => void;
  toggleRoom: (room: SelectionItem) => void;
  replaceRoom: (oldSlug: string, newRoom: SelectionItem) => void;
  setSingleRoomCheckout: (value: boolean) => void;
  /** Clear room items and single-room flag, but preserve dates. */
  clearSelection: () => void;
  clearAll: () => void;
}

export const useBookingStore = create<BookingStore>()(
  persist(
    (set, get) => ({
      checkIn: null,
      checkOut: null,
      items: [],
      singleRoomCheckout: false,

      setDates: (checkIn, checkOut) =>
        set({ checkIn, checkOut }),

      addRoom: (room) => {
        const state = get();
        if (state.items.some((i) => i.roomSlug === room.roomSlug)) return;
        set({ items: [...state.items, room] });
        trackAdd(room, state.checkIn, state.checkOut);
      },

      removeRoom: (roomSlug) =>
        set((state) => ({
          items: state.items.filter((i) => i.roomSlug !== roomSlug),
        })),

      toggleRoom: (room) => {
        const state = get();
        const exists = state.items.some((i) => i.roomSlug === room.roomSlug);
        set({
          items: exists
            ? state.items.filter((i) => i.roomSlug !== room.roomSlug)
            : [...state.items, room],
        });
        if (!exists) trackAdd(room, state.checkIn, state.checkOut);
      },

      replaceRoom: (oldSlug, newRoom) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.roomSlug === oldSlug ? newRoom : i
          ),
        })),

      setSingleRoomCheckout: (value) => set({ singleRoomCheckout: value }),

      clearSelection: () => set({ items: [], singleRoomCheckout: false }),

      clearAll: () => set({ checkIn: null, checkOut: null, items: [], singleRoomCheckout: false }),
    }),
    {
      name: 'booking-selection',
      storage: createJSONStorage(() => createExpiringSessionStorage(ONE_DAY_MS)),
    }
  )
);
